/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useMemo } from "react";
import type { FunctionComponent, JSX } from "react";

import type { TMyHubActionType } from "../../../viewModel/myHub/myHubActionTypes";
import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../viewModel";
import { performMyHubAction } from "../../../viewModel/myHub/myHubActionHandlers";
import {
  MY_HUB_SCREEN_ID,
  MY_HUB_SCREEN_NAME,
  myHubTabAnalyticsLogger,
  selectMyHubSelectedFilter
} from "../../../viewModel/myHub";
import type { MyHubFilterId } from "../../../viewModel/myHub";
import {
  dismissRecordingHighlight,
  finishRecordingHighlightDismiss,
  focusRecordingHighlightAction,
  selectIsRecordingHighlightClosing,
  selectIsRecordingHighlightVisible,
  selectRecordingHighlightFocusedActionIndex,
  selectRecordingHighlightItem,
  selectRecordingHighlightListName,
  selectRecordingHighlightListPosition,
  selectRecordingHighlightTilePosition
} from "../../../viewModel/myHub/recordingHighlight";
import type { TMyHubRecordingHighlightItem } from "../../../viewModel/myHub/recordingHighlight";
import { useModalController } from "../../hooks";
import { useNavigationService } from "../../hooks";
import { MyHubRecordingHighlightController } from "./MyHubRecordingHighlightController";
import MyHubRecordingHighlight from "./MyHubRecordingHighlight";

const TAG: string = "MyHubRecordingHighlightContainer";

/**
 * How long the sheet is kept mounted after a dismiss, so it can sink off screen
 * before it is torn down.
 *
 * Mirrors `$mrh-slide-out-duration` in
 * `_my-hub-recording-highlight-tokens.scss`; the two are read by different
 * languages and have to be changed together.
 */
const DISMISS_TRANSITION_MS: number = 200;

/**
 * Connected container for the recording highlight popup.
 *
 * Mounted once in the app shell (`App.tsx`) rather than by a screen, so any
 * screen can raise the popup with `dispatch(showRecordingHighlight(item))` and
 * none of them has to host it. While it is open a
 * {@link MyHubRecordingHighlightController} sits on the active screen's modal
 * stack and consumes every D-pad press.
 */
const MyHubRecordingHighlightContainer: FunctionComponent =
  (): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
    const CONTEXT: string = TAG;
    const dispatch = useUnityDispatch();
    const { navigateTo } = useNavigationService();

    const isVisible: boolean = useUnitySelector(
      selectIsRecordingHighlightVisible
    );
    const isClosing: boolean = useUnitySelector(
      selectIsRecordingHighlightClosing
    );
    const item: TMyHubRecordingHighlightItem | null = useUnitySelector(
      selectRecordingHighlightItem
    );
    const focusedActionIndex: number = useUnitySelector(
      selectRecordingHighlightFocusedActionIndex
    );

    /**
     * Unmounts the popup once the sink has played out.
     *
     * A timer rather than `transitionend`: that event never fires under
     * `prefers-reduced-motion`, where the SCSS drops the transition, and it does
     * not fire for a popup unmounted mid-transition either — the sheet would be
     * left on screen for good. The cleanup drops a pending unmount when
     * `isClosing` goes false, which is what lets a reopen mid-dismiss survive.
     */
    useEffect((): (() => void) | undefined => {
      if (!isClosing) {
        return undefined;
      }

      const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
        dispatch(finishRecordingHighlightDismiss());
      }, DISMISS_TRANSITION_MS);

      return (): void => {
        clearTimeout(timer);
      };
    }, [dispatch, isClosing]);

    const handleDismiss = useCallback((): void => {
      dispatch(dismissRecordingHighlight());
    }, [dispatch]);

    const handleActionFocus = useCallback(
      (index: number): void => {
        dispatch(focusRecordingHighlightAction(index));
      },
      [dispatch]
    );

    /**
     * The one place an action is carried out, shared by the pointer path and the
     * controller's NAV_OK.
     *
     * The item is read from the store rather than closed over so this callback
     * stays referentially stable across opens — the controller is built once and
     * must not be rebuilt every time a different recording is shown.
     *
     * What each action *does* is not decided here: the press is handed to
     * `performMyHubAction`, the one table every My Hub surface shares, so this
     * popup and the flat-list rows cannot drift into two implementations of the
     * same button. Dismissal is passed *in* rather than called afterwards, so an
     * action that navigates closes the popup first and one that declines leaves
     * it open.
     */
    const handleActionSelect = useCallback(
      (actionType: TMyHubActionType): void => {
        const state = UnityAppStore.getState();
        const current: TMyHubRecordingHighlightItem | null =
          selectRecordingHighlightItem(state);
        if (current === null) {
          return;
        }

        // The popup only opens from the carousel tabs (Recorded/Scheduled),
        // so the selected tab at press time is this event's screen.
        const selectedFilter: MyHubFilterId = selectMyHubSelectedFilter(state);
        // The carousel row this popup was raised from — captured by
        // openRecordingHighlight() at open time and carried on the
        // recordingHighlight slice, since the tile itself does not know its
        // own row's title/position.
        void performMyHubAction(actionType, current.tile, {
          dispatch,
          navigateTo,
          dismissSurface: (): void => {
            dispatch(dismissRecordingHighlight());
          },
          tag: TAG,
          screenId: MY_HUB_SCREEN_ID,
          screenName: MY_HUB_SCREEN_NAME,
          analyticsLogger: myHubTabAnalyticsLogger(selectedFilter),
          listName: selectRecordingHighlightListName(state),
          listPosition: selectRecordingHighlightListPosition(state),
          tilePosition: selectRecordingHighlightTilePosition(state)
        });
      },
      [dispatch, navigateTo]
    );

    const modalController = useMemo(
      (): MyHubRecordingHighlightController =>
        new MyHubRecordingHighlightController(
          dispatch,
          UnityAppStore.getState,
          handleActionSelect
        ),
      [dispatch, handleActionSelect]
    );

    // Keyed on `isVisible` alone, so the controller comes off the modal stack
    // the moment the dismiss starts: the sheet is still sinking, but the screen
    // behind it already owns the D-pad.
    useModalController(modalController, isVisible);

    // A closing popup stays mounted until its transition is done. `item === null`
    // is checked alongside the flags so the popup can never render a half-open
    // frame between the state fields settling.
    if ((!isVisible && !isClosing) || item === null) {
      return null;
    }

    return (
      <MyHubRecordingHighlight
        item={item}
        isClosing={isClosing}
        focusedActionIndex={focusedActionIndex}
        onActionFocus={handleActionFocus}
        onActionSelect={handleActionSelect}
        onDismiss={handleDismiss}
      />
    );
  };

export default MyHubRecordingHighlightContainer;
